import { spawnQuad, spawnSprite, updateVfxPool } from './vfx-pool';
import { texChunk, texDust, texFire, texFlash, texSmoke, texSpark } from './vfx-tex';
import { updateScreenFlash } from './screen-flash';
import { beginFrame, take } from './vfx-budget';
import { activeStyle } from './vfx-style';
/* ============================================================
   武器特效的配方（實彈／軍械）
   ============================================================

   使用者 2026-08-05 第三次定調：
   「**泛光太重，看起來很像魔法攻擊；我要的是機甲飛彈那種打擊感跟畫面效果**」。

   前兩版（動漫機甲 VFX、極簡霓虹幾何）都被否掉，共同的病因是**加法混色的
   大面積光**：任何超過泛光門檻 0.66 的東西都會被 UnrealBloom 糊成光暈，
   而光暈就是魔法。這一版的原則整個翻過來。

   ## 四條規則

   1. **會發光的東西又小又短**：火光、火星、彈道 —— 面積小、壽命 < 0.12 秒。
      真正的槍口火焰在現實裡也只有一兩格底片看得到。
   2. **畫面上的量體是煙與塵，走一般混色**：它們**遮住**背後的東西。
      光只會疊加，煙會擋住 —— 那就是「有實體」的全部差別。
   3. **火星要吃重力**：往上拋、往下落、落地前熄掉。飄浮不落的火星是魔法。
   4. **地面回饋是揚塵不是光環**：擴散的幾何環是法陣的語言。
      塵是不規則的、暗的、慢的，而且會留在原地一秒鐘。

   ## 色譜只有三段

   白熱（瞬間）→ 橙（火）→ 灰褐（煙與塵）。**沒有青、藍、紫。**
   冷色是能量武器的語言，這一版只有大招光束例外（它本來就是能量武器），
   而且連它都被壓到幾乎不發光。

   ## 每幀配額

   300 隻同屏時「每次命中都生三層特效」會瞬間吃光池子，而且那一幀的
   draw call 會爆。所以每一幀有一個總配額，超過就**整個事件跳過**
   （不是畫一半）—— 少一個爆點沒人看得出來，畫一半的爆點很明顯。 */

/** 語意色彩。三段，全部是暖色或中性色。 */
export var VC = {
  /** 白熱：槍口與爆心的那一瞬間。**只有這個顏色可以過泛光門檻**，
      而且只准出現在直徑一公尺以內、壽命 0.1 秒以下的東西上。 */
  hot: 0xffe7bd,
  /** 火：橙。爆炸與曳光。 */
  fire: 0xff8f3a,
  /** 火星：偏黃的橙，比火亮一階（它是熾熱的金屬碎屑）。 */
  spark: 0xffc06a,
  /** 煙：偏冷的灰。**比場景亮一點點就夠** —— 太亮會變成雲，太暗會消失。 */
  smoke: 0x8b929c,
  /** 塵：偏褐的灰。地面掀起來的是土不是煙，色相要往暖偏。 */
  dust: 0x9d9382
};

/** 玩家武器特效的不透明度上限（06 手感規格是 0.75）。
    這一版**加法類**壓在 0.5 以下（泛光門檻 0.66，過了就是魔法），
    煙塵那類走一般混色，反而可以到 0.4–0.5 而不會刺眼。 */
export var OP_MAX = 0.5;

/* 每幀配額搬到 `vfx-budget.ts`（風格模組也要用它，留在這裡會形成循環 import）。
   實測數字與理由見那個檔的檔頭。 */

/** 每畫面幀重置配額、推進延遲佇列與池子。`dt` 已經乘過 `STATE.timeScale`。 */
export function updateWeaponFx(dt: number) { beginFrame(dt); updateVfxPool(dt); updateScreenFlash(dt); }

/** 世界角度 → 躺平 quad 的 yaw。推導見 `vfx-pool.ts` 的 `YXZ` 註解：
    quad 的局部 +X 要對準 (sin a, 0, cos a)，解出來就是 a − π/2。 */
function yawOf(ang: number) { return ang - Math.PI / 2; }
var FLAT = -Math.PI / 2;

function rnd(a: number, b: number) { return a + Math.random() * (b - a); }

/* ---------- 共用零件 ---------- */

/** 火星：吃重力、有阻力、落地前就熄掉。**這是實彈感最便宜的來源** ——
    一團往外炸又掉下去的碎屑，比任何光環都像「有東西被打中了」。 */
export function fxSparks(x: number, y: number, z: number, n: number,
  ang: number, spread: number, color: number, speed: number) {
  if (!take(n)) return;
  for (var i = 0; i < n; i++) {
    var a = ang + rnd(-spread, spread);
    var v = speed * rnd(0.45, 1.35);
    spawnQuad(texSpark(), x, y, z, FLAT, yawOf(a + Math.PI), 0, {
      c: color, s: rnd(1.4, 3.0), s1: rnd(0.3, 0.7), ar: 0.055,
      t: rnd(0.24, 0.46), op: 0.5, pow: 1.6, ease: 1,
      vx: Math.sin(a) * v, vy: rnd(2, 7), vz: Math.cos(a) * v, drag: 2.6, grav: 26
    });
  }
}

/** 煙：一般混色，會遮住背後的東西。往上飄、慢慢散開。 */
export function fxSmoke(x: number, y: number, z: number, n: number,
  s0: number, s1: number, spread: number, life: number, op: number) {
  if (!take(n)) return;
  for (var i = 0; i < n; i++) {
    var a = rnd(0, 6.283);
    spawnSprite(texSmoke(), x + rnd(-0.3, 0.3), y, z + rnd(-0.3, 0.3), {
      normal: true, c: VC.smoke, s: s0 * rnd(0.8, 1.2), s1: s1 * rnd(0.8, 1.3),
      t: life * rnd(0.75, 1.25), op: op, pow: 1.5, ease: 1.6,
      rot: rnd(0, 6.283), spin: rnd(-0.9, 0.9),
      vx: Math.cos(a) * spread, vy: rnd(0.8, 2.4), vz: Math.sin(a) * spread, drag: 1.9
    });
  }
}

/** 碎片：不規則實心塊，吃重力、會自轉。一般混色（它是實體）。 */
export function fxChunks(x: number, y: number, z: number, n: number, color: number, speed: number, size: number) {
  if (!take(n)) return;
  for (var i = 0; i < n; i++) {
    var a = rnd(0, 6.283), v = speed * rnd(0.5, 1.2);
    spawnSprite(texChunk(), x, y, z, {
      normal: true, c: color, s: size * rnd(0.55, 1.15), s1: size * rnd(0.4, 0.9),
      t: rnd(0.35, 0.7), op: 0.75, pow: 2.2, ease: 1,
      rot: rnd(0, 6.283), spin: rnd(-11, 11),
      vx: Math.cos(a) * v, vy: rnd(3, 9), vz: Math.sin(a) * v, drag: 1.4, grav: 30
    });
  }
}

/** 地面揚塵。**取代上一版的擴散幾何環**：爆炸在地上留下的是一圈被掀起來的灰，
    不是一個發光的圓。慢、暗、留得久。 */
export function fxDust(x: number, z: number, r0: number, r1: number, life: number, op?: number) {
  if (!take(2)) return;
  spawnQuad(texDust(), x, 0.12, z, FLAT, rnd(0, 6.283), 0, {
    normal: true, c: VC.dust, s: r0 * 2, s1: r1 * 2, t: life,
    op: op === undefined ? 0.34 : op, pow: 1.8, ease: 2.4
  });
}

/* ---------- 槍口 ---------- */

/** 開火。`power` 0.35（主砲連射）～1.0（大招）。

    四拍：白熱星芒（0.06 秒）→ 沿槍管噴出的火（0.08 秒）→ 反衝火星 →
    一小團被推出去的煙。**沒有環、沒有十字、沒有光暈**。 */
export function fxMuzzle(x: number, y: number, z: number, ang: number, power: number) {
  /* 候選風格的分派點（`render/vfx-style.ts`）。預設是 `v3` ——
     它的 `muzzle` 是 undefined，所以遊戲跑的仍然是下面這一套。
     切換只會發生在 `/vfx-preview.html` 或明確呼叫 `setStyle()` 之後。 */
  var st = activeStyle();
  if (st.muzzle) return st.muzzle(x, y, z, ang, power);
  var big = power > 0.7;
  if (!take(big ? 8 : 3)) return;
  /* 尺寸放大一輪（2026-08-05 實拍後）：第一版的星芒只有 0.6 公尺，
     在遊戲鏡頭下小到看不見。**重量感來自量體不是亮度** ——
     放大而不加不透明度，才不會又回到泛光過重。 */
  var s = 1.7 + power * 3.2;
  // ① 星芒：不對稱、極短。這一層是「打出去的那一下」
  spawnSprite(texFlash(), x, y, z, {
    c: VC.hot, s: s * 0.7, s1: s * 1.3, t: 0.06 + power * 0.05,
    op: 0.9, pow: 1.4, rot: rnd(0, 6.283)
  });
  // ② 槍管前方的火：沿射向拉長，很快被拖掉
  spawnQuad(texSpark(), x + Math.sin(ang) * s * 0.4, y, z + Math.cos(ang) * s * 0.4,
    FLAT, yawOf(ang + Math.PI), 0, {
    c: VC.fire, s: s * 1.5, s1: s * 0.5, ar: 0.40, t: 0.08 + power * 0.06, op: 0.45, pow: 1.8
  });
  // ③ 反衝火星：往射向的反側甩，吃重力
  fxSparks(x, y, z, big ? 7 : 3, ang + Math.PI * 0.85, 0.7, VC.spark, big ? 16 : 10);
  if (!big) return;
  fxSmoke(x, y + 0.2, z, 2, s * 0.5, s * 1.9, 3.0, 0.7, 0.30);
}

/* ---------- 命中 ---------- */

/** 一般命中。**這是現況完全沒有的東西** —— 打中敵人只有傷害數字與音效，
    沒有任何畫面回饋，所以「打得到」跟「打不到」看起來一樣。

    實彈的命中不是一圈光，是**火星從裝甲上濺開**：一點極小的白熱、
    三四道往回彈的火星、一小片碎屑。暴擊多給火星與一片碎片，不換顏色。 */
export function fxImpact(x: number, y: number, z: number, ang: number, crit: boolean) {
  var st = activeStyle();
  if (st.impact) return st.impact(x, y, z, ang, crit);
  if (!take(crit ? 8 : 4)) return;
  spawnSprite(texFlash(), x, y, z, {
    c: VC.hot, s: crit ? 1.6 : 0.95, s1: crit ? 2.8 : 1.6, t: 0.08,
    op: crit ? 0.8 : 0.62, pow: 1.6, rot: rnd(0, 6.283)
  });
  fxSparks(x, y, z, crit ? 9 : 4, ang + Math.PI, crit ? 1.1 : 0.75, VC.spark, crit ? 17 : 11);
  if (!crit) return;
  // 暴擊：打下一片裝甲。碎片是實體（一般混色），所以它會遮住敵人 —— 那就是「打進去了」
  fxChunks(x, y, z, 3, 0x8d8a84, 9, 0.36);
}

/** 爆炸（飛彈／地雷／自爆）。**這一招是整個方向的代表**，所以層次最完整：

    白熱核心（0.09 秒）→ 橙色火球（0.22 秒）→ 往外飛的碎片與火星 →
    **灰煙柱**（1 秒以上，一般混色，會擋住背後）→ **地面揚塵環**。
    火退得快、煙留得久 —— 那個時間差就是「剛剛這裡炸過」的證據。 */
export function fxExplode(x: number, y: number, z: number, color: number, r: number, power: number) {
  var st = activeStyle();
  if (st.explode) return st.explode(x, y, z, color, r, power);
  if (!take(14)) return;
  spawnSprite(texFlash(), x, y, z, {
    c: VC.hot, s: r * 0.7, s1: r * 1.35, t: 0.10, op: 0.85, pow: 1.5, rot: rnd(0, 6.283)
  });
  spawnSprite(texFire(), x, y, z, {
    c: VC.fire, s: r * 0.6, s1: r * 2.2, t: 0.26, op: OP_MAX, pow: 2.0, ease: 2.6
  });
  fxChunks(x, y, z, 5, 0x7e7a73, r * 3.4, r * 0.30);
  fxSparks(x, y, z, 6, 0, 3.15, VC.spark, r * 3.6);
  fxSmoke(x, y + 0.3, z, 4, r * 0.8, r * 2.8, r * 1.2, 1.25, 0.46);
  fxDust(x, z, r * 0.6, r * 3.0 * power, 1.0, 0.42);
}

/* ---------- 近戰／範圍 ---------- */

/** 斬擊。實彈風格裡近戰不該發光 —— 改成**一道很快的刃光 + 被掃起來的塵**。
    刃光只有 0.1 秒、寬度很窄；量體交給地上那一道弧形塵。 */
export function fxSlashArc(x: number, y: number, z: number, yaw: number, range: number, color: number) {
  var st = activeStyle();
  if (st.slashArc) return st.slashArc(x, y, z, yaw, range, color);
  if (!take(8)) return;
  // 刃光：沿揮砍方向的一道細長弧（用火星貼圖拉寬，端點是尖的）
  spawnQuad(texSpark(), x + Math.sin(yaw) * range * 0.55, y, z + Math.cos(yaw) * range * 0.55,
    FLAT, yawOf(yaw + Math.PI / 2), 0, {
    c: VC.hot, s: range * 2.1, s1: range * 2.5, ar: 0.16, t: 0.10, op: 0.55, pow: 1.4
  });
  fxDust(x + Math.sin(yaw) * range * 0.5, z + Math.cos(yaw) * range * 0.5,
    range * 0.7, range * 1.5, 0.5, 0.26);
  for (var i = 0; i < 4; i++) {
    var a = yaw + (i - 1.5) * 0.44;
    fxSparks(x + Math.sin(a) * range * 0.7, y, z + Math.cos(a) * range * 0.7, 1, a, 0.3, VC.spark, 13);
  }
}

/** 地面衝擊：一圈揚塵。名字沿用（呼叫端很多），但它已經不是「環」了。 */
export function fxShockRing(x: number, z: number, color: number, r0: number, r1: number, t: number, op?: number) {
  var st = activeStyle();
  if (st.shockRing) return st.shockRing(x, z, color, r0, r1, t, op);
  fxDust(x, z, r0, r1, t + 0.2, op === undefined ? 0.30 : op * 0.55);
}

/** 發射（飛彈齊射、掛架開架）：一團被推出去的白煙 + 幾點火星。
    **沒有環** —— 飛彈離架的畫面裡地上不會出現一個圓。 */
export function fxLaunch(x: number, y: number, z: number, ang: number, color: number) {
  var st = activeStyle();
  if (st.launch) return st.launch(x, y, z, ang, color);
  if (!take(7)) return;
  spawnSprite(texFlash(), x, y, z, {
    c: VC.hot, s: 1.2, s1: 2.1, t: 0.07, op: 0.65, pow: 1.5, rot: rnd(0, 6.283)
  });
  fxSmoke(x, y, z, 4, 1.2, 5.6, 4.6, 1.0, 0.44);
  fxSparks(x, y, z, 3, ang + Math.PI, 1.0, VC.spark, 11);
}

/* ---------- 大招 ---------- */

/** 大招發射的那一拍。使用者選的方向是「加 0.35 秒鏡頭演出」，
    所以這裡負責畫面，時間縮放與推鏡由 `sim/weapons.ts` 一起下。

    這一版把它當**重砲的後座**處理而不是能量爆發：
    槍口一團大火 + 大量往後噴的火星與煙 + 腳下被吹開的塵。
    往內收的線段（上一版的「假前搖」）拿掉了 —— 那是能量匯聚的語言。 */
export function fxUltFire(x: number, y: number, z: number, ang: number, color: number) {
  var st = activeStyle();
  if (st.ultFire) return st.ultFire(x, y, z, ang, color);
  if (!take(24)) return;
  spawnSprite(texFlash(), x, y, z, {
    c: VC.hot, s: 3.6, s1: 7.0, t: 0.11, op: 0.85, pow: 1.5, rot: rnd(0, 6.283)
  });
  spawnSprite(texFire(), x + Math.sin(ang) * 1.6, y, z + Math.cos(ang) * 1.6, {
    c: VC.fire, s: 3.0, s1: 9.0, t: 0.28, op: OP_MAX, pow: 2.0, ease: 2.6
  });
  // 後座：火星與煙往射向的反側噴，讀成「整台被推退」
  fxSparks(x, y, z, 8, ang + Math.PI, 0.9, VC.spark, 22);
  fxSmoke(x - Math.sin(ang) * 1.5, y + 0.4, z - Math.cos(ang) * 1.5, 5, 2.2, 11, 5.0, 1.4, 0.44);
  // 腳下的塵被吹開：兩圈不同速度
  fxDust(x, z, 2.5, 16, 0.85, 0.44);
  fxDust(x, z, 4.0, 26, 1.4, 0.26);
  fxChunks(x, 0.5, z, 4, 0x8a857c, 12, 0.35);
}

/** 光束掃到東西時的落點。每 0.07 秒的傷害節拍打一次，所以要**很便宜**。
    改成「被燒到的地方濺出火星」而不是又一圈光。 */
export function fxBeamHit(x: number, y: number, z: number, color: number) {
  var st = activeStyle();
  if (st.beamHit) return st.beamHit(x, y, z, color);
  if (!take(3)) return;
  spawnSprite(texFlash(), x, y, z, {
    c: VC.hot, s: 0.9, s1: 1.6, t: 0.08, op: 0.55, pow: 1.6, rot: rnd(0, 6.283)
  });
  fxSparks(x, y, z, 3, 0, 3.15, VC.spark, 12);
}

/* ---------- 副武裝（掛載）---------- */

/** 每把掛載的**招牌色**。十把武裝在畫面上分不出誰是誰 ——
    子彈共用同一組實例化材質（顏色改不了），所以區辨只能靠發射的那一拍。

    這一版全部落在**暖色帶**（黃 → 橙 → 紅褐）：冷色是能量武器的語言，
    而這些是實彈。分辨度靠色相的細微差，不靠跨色系。 */
export var GADGET_C: Record<string, number> = {
  'w-mine': 0xff9a3c, 'w-orbital': 0xffd08a, 'w-chain': 0xffc266,
  'w-pulse': 0xffab4e, 'w-orbit': 0xd8dee6, 'w-drone': 0xffd9a8,
  'w-aura': 0xc9c2b4, 'w-rear': 0xff8a4a, 'w-tether': 0xbfae8e,
  'w-ricochet': 0xffe08a
};

/** 掛載開火：比主砲小一號的星芒 + 火星。 */
export function fxGadgetFire(id: string, x: number, y: number, z: number, ang: number) {
  var st = activeStyle();
  if (st.gadgetFire) return st.gadgetFire(id, x, y, z, ang);
  var c = GADGET_C[id] || VC.spark;
  if (!take(4)) return;
  spawnSprite(texFlash(), x, y, z, {
    c: c, s: 1.1, s1: 1.9, t: 0.06, op: 0.62, pow: 1.5, rot: rnd(0, 6.283)
  });
  fxSparks(x, y, z, 3, ang + Math.PI, 0.7, VC.spark, 10);
}

/** 軌道光柱落地。**這是全遊戲最重的一擊**，所以塵與碎片給最多。 */
export function fxOrbitalHit(x: number, z: number, r: number, color: number) {
  var st = activeStyle();
  if (st.orbitalHit) return st.orbitalHit(x, z, r, color);
  if (!take(16)) return;
  spawnSprite(texFlash(), x, 1.2, z, {
    c: VC.hot, s: r * 0.55, s1: r * 1.1, t: 0.10, op: 0.85, pow: 1.5, rot: rnd(0, 6.283)
  });
  spawnSprite(texFire(), x, 1.0, z, {
    c: VC.fire, s: r * 0.5, s1: r * 1.7, t: 0.26, op: OP_MAX, pow: 2.0, ease: 2.6
  });
  fxDust(x, z, r * 0.6, r * 3.0, 1.0, 0.38);
  fxSmoke(x, 0.8, z, 4, r * 0.5, r * 1.8, r * 0.9, 1.3, 0.38);
  fxChunks(x, 0.6, z, 5, 0x847f76, r * 2.2, r * 0.24);
  fxSparks(x, 0.6, z, 4, 0, 3.15, VC.spark, r * 2.6);
}

/** 飛彈排氣：沿飛行路徑一路留下的小煙團。

    **取代原本的加法緞帶**（2026-08-05：使用者回報飛彈魔法感太重）。
    緞帶是一條會發光的線 —— 不論調到多暗，它都是「一道光劃過去」。
    真實的飛彈留下的是**一串會擴散、會擋住背後、慢慢散掉的煙**，
    而且是一團一團的（燃燒不均勻），不是一條連續的帶。 */
export function fxExhaust(x: number, y: number, z: number, scale: number) {
  var st = activeStyle();
  if (st.exhaust) return st.exhaust(x, y, z, scale);
  if (!take(1)) return;
  spawnSprite(texSmoke(), x, y, z, {
    /* 排氣煙比一般的煙**亮一階也濃一階**（使用者 2026-08-05：飛彈再顯眼些）。
       飛彈的辨識度不能靠彈體發光，只能靠「它後面拖著一條白」—— 那條白
       在深色場景裡是最強的訊號，而且它是一般混色，不會加進泛光。 */
    normal: true, c: 0xc6ccd4, s: 0.85 * scale, s1: 4.0 * scale,
    t: rnd(0.7, 1.15), op: 0.5, pow: 1.5, ease: 1.5,
    rot: rnd(0, 6.283), spin: rnd(-0.8, 0.8),
    vy: rnd(0.3, 1.1), drag: 1.6
  });
}
